import Phaser from 'phaser'
import type { MatchEngine } from '@engine/MatchEngine'
import { TEXTURE_KEYS } from '@interfaces/index'
import { cartToIso, isoToCart } from '@engine/Grid'
import { calcCellSize, calcGridOrigin } from './isoUtils'
import { AssetRegistry } from '../AssetRegistry'

// Rotate grid coords by step (0=N, 1=E, 2=S, 3=W)
export function rotateCoord(
  gx: number, gy: number,
  cols: number, rows: number,
  step: number
): { gx: number; gy: number } {
  let rx = gx, ry = gy
  for (let i = 0; i < (step & 3); i++) {
    const tmp = rx
    rx = rows - 1 - ry
    ry = tmp
    const t = cols; cols = rows; rows = t   // eslint-disable-line no-param-reassign
  }
  return { gx: rx, gy: ry }
}

export class GridRenderer {
  private graphics: Phaser.GameObjects.Graphics
  private pathGraphics: Phaser.GameObjects.Graphics
  private rangeRingGraphics: Phaser.GameObjects.Graphics
  private highlightedCell: { gx: number; gy: number } | null = null
  private rangeRing: { gx: number; gy: number; range: number } | null = null
  private readonly scene: Phaser.Scene
  private readonly engine: MatchEngine
  private playerId: string
  private rotationStep: number = 0   // 0=N 1=E 2=S 3=W

  // Tile sprite layer — used when real tile assets are loaded
  private tileSprites: Map<string, Phaser.GameObjects.Image> = new Map()
  // Track whether we already built the sprite layer so we don't recreate it every frame
  private _tileSpritesBuilt = false

  // Dirty flag: only redraw the procedural grid when something actually changed.
  // Set to true on tower placement/demolition, highlight changes, and resize.
  private _gridDirty = true

  // Public so TowerRenderer / UnitRenderer can read them after each resize
  cellW: number
  cellH: number
  originX: number
  originY: number

  constructor(scene: Phaser.Scene, engine: MatchEngine, playerId: string) {
    this.scene = scene
    this.engine = engine
    this.playerId = playerId

    // Initial sizing
    const map = engine.config.map
    const { cellW, cellH } = calcCellSize(
      scene.scale.width,
      scene.scale.height,
      map.cols,
      map.rows
    )
    this.cellW = cellW
    this.cellH = cellH
    const { x: originX, y: originY } = calcGridOrigin(
      scene.scale.width,
      scene.scale.height,
      map.cols,
      map.rows,
      cellW,
      cellH
    )
    this.originX = originX
    this.originY = originY

    // Recompute layout whenever Phaser resizes the canvas
    scene.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      this._recalcLayout(gameSize.width, gameSize.height)
      // Force tile sprite rebuild on resize so positions update
      this._tileSpritesBuilt = false
      this._gridDirty = true
    })

    this.graphics = scene.add.graphics()
    this.pathGraphics = scene.add.graphics()
    this.rangeRingGraphics = scene.add.graphics()
    this.rangeRingGraphics.setDepth(12) // above units, below projectiles

    // Mark dirty whenever tower layout changes so the grid redraws on the next frame.
    engine.eventBus.on('TOWER_PLACED',     () => { this._gridDirty = true })
    engine.eventBus.on('TOWER_DEMOLISHED', () => { this._gridDirty = true })
    engine.eventBus.on('BASE_UPGRADED',    () => { this._gridDirty = true })
  }

  /** Show or hide a range ring at the given grid cell. Null clears it. */
  setRangeRing(ring: { gx: number; gy: number; range: number } | null): void {
    this.rangeRing = ring
  }

  private _recalcLayout(screenW: number, screenH: number): void {
    const map = this.engine.config.map
    const { cellW, cellH } = calcCellSize(screenW, screenH, map.cols, map.rows)
    this.cellW = cellW
    this.cellH = cellH
    const { x: originX, y: originY } = calcGridOrigin(
      screenW,
      screenH,
      map.cols,
      map.rows,
      cellW,
      cellH
    )
    this.originX = originX
    this.originY = originY
  }

  /**
   * Build or reposition tile sprites for the full grid.
   * Called lazily on first update when tile assets are loaded, and again after resize.
   * Procedural Graphics rendering is used when tiles are not loaded.
   */
  private _buildTileSprites(): void {
    const grid = this.engine.getGrid(this.playerId)
    if (grid === undefined) return

    const cells = grid.getCells()
    const { cellW, cellH, originX, originY } = this
    const scale = cellW / 128  // source tiles are 128×64px

    for (let gy = 0; gy < cells.length; gy++) {
      const row = cells[gy]
      if (row === undefined) continue
      for (let gx = 0; gx < row.length; gx++) {
        const cell = row[gx]
        if (cell === undefined) continue

        const r = this.rot(gx, gy)
        const iso = cartToIso(r.gx, r.gy, cellW, cellH)
        const sx = iso.x + originX
        const sy = iso.y + originY

        const tileKey = cell.type === 'obstacle'
          ? TEXTURE_KEYS.TILE_OBSTACLE
          : TEXTURE_KEYS.TILE_GRASS

        const spriteId = `${gx},${gy}`
        let sprite = this.tileSprites.get(spriteId)
        if (sprite === undefined) {
          sprite = this.scene.add.image(sx, sy, tileKey)
          sprite.setDepth(0)
          sprite.setScale(scale, scale)
          this.tileSprites.set(spriteId, sprite)
        } else {
          sprite.setPosition(sx, sy)
          sprite.setTexture(tileKey)
        }
      }
    }

    // Render base and spawn markers if available
    const map = this.engine.config.map
    if (AssetRegistry.hasTileSprite(TEXTURE_KEYS.SPAWN_MARKER)) {
      const rs = this.rot(map.spawnX, map.spawnY)
      const spawnIso = cartToIso(rs.gx, rs.gy, cellW, cellH)
      const spawnKey = 'spawn_marker_overlay'
      let spawnSprite = this.tileSprites.get(spawnKey)
      if (spawnSprite === undefined) {
        spawnSprite = this.scene.add.image(
          spawnIso.x + originX, spawnIso.y + originY, TEXTURE_KEYS.SPAWN_MARKER
        )
        spawnSprite.setDepth(1)
        spawnSprite.setScale(scale * 0.5)
        this.tileSprites.set(spawnKey, spawnSprite)
      } else {
        spawnSprite.setPosition(spawnIso.x + originX, spawnIso.y + originY)
      }
    }
    const baseTexKey = TEXTURE_KEYS.BASE_PLAYER
    if (AssetRegistry.hasTileSprite(baseTexKey)) {
      const rb = this.rot(map.baseX, map.baseY)
      const baseIso = cartToIso(rb.gx, rb.gy, cellW, cellH)
      const baseKey = 'base_player_overlay'
      let baseSprite = this.tileSprites.get(baseKey)
      if (baseSprite === undefined) {
        baseSprite = this.scene.add.image(
          baseIso.x + originX, baseIso.y + originY, baseTexKey
        )
        baseSprite.setDepth(1)
        baseSprite.setScale(scale)
        this.tileSprites.set(baseKey, baseSprite)
      } else {
        baseSprite.setPosition(baseIso.x + originX, baseIso.y + originY)
      }
    }

    this._tileSpritesBuilt = true
  }

  /** Destroy all tile sprite objects (called on resize or destroy). */
  private _clearTileSprites(): void {
    for (const [, sprite] of this.tileSprites) {
      sprite.destroy()
    }
    this.tileSprites.clear()
    this._tileSpritesBuilt = false
  }

  update(): void {
    // Skip the full redraw when nothing has changed.
    // The dirty flag is set on: tower placed/demolished, highlight changes, resize, spy switch.
    if (!this._gridDirty) {
      // Range ring still redraws every frame (it can appear/disappear during card hover)
      this._updateRangeRing()
      return
    }
    this._gridDirty = false

    this.graphics.clear()
    this.pathGraphics.clear()

    const grid = this.engine.getGrid(this.playerId)
    if (grid === undefined) return

    // Zero-copy read: getCells() returns the internal array reference (no 280-object clone).
    const cells = grid.getCells()
    const map = this.engine.config.map
    const { cellW, cellH, originX, originY } = this
    const hw = cellW / 2
    const hh = cellH / 2

    // ── Tile sprite path: use real tile assets when available ─────────────────
    const hasTiles = AssetRegistry.hasTileSprite(TEXTURE_KEYS.TILE_GRASS)
    if (hasTiles) {
      if (!this._tileSpritesBuilt) {
        this._buildTileSprites()
      }
      // Highlight overlay is always drawn via Graphics on top of the tile sprites
      if (this.highlightedCell !== null) {
        const r = this.rot(this.highlightedCell.gx, this.highlightedCell.gy)
        const iso = cartToIso(r.gx, r.gy, cellW, cellH)
        const sx = iso.x + originX
        const sy = iso.y + originY
        this.graphics.fillStyle(0xffff00, 0.5)
        this.graphics.beginPath()
        this.graphics.moveTo(sx, sy - hh)
        this.graphics.lineTo(sx + hw, sy)
        this.graphics.lineTo(sx, sy + hh)
        this.graphics.lineTo(sx - hw, sy)
        this.graphics.closePath()
        this.graphics.fillPath()
      }
    } else {
      // ── Procedural fallback ─────────────────────────────────────────────────
      // If tile sprites were previously built (e.g. assets unloaded), clear them.
      if (this._tileSpritesBuilt) this._clearTileSprites()

      // Two-pass rendering to minimise GPU draw calls:
      //
      // Pass 1 — batch all empty-cell outlines into one beginPath/strokePath.
      //   Reduces ~260 individual strokePath calls down to 1.
      //
      // Pass 2 — draw each special cell (obstacle/spawn/base/tower/highlight) with
      //   its own fillStyle+fillPath.  These are typically ≤ 25 cells out of 280.
      //
      // The highlighted cell is treated as a special cell in pass 2 so it renders
      // on top of the outline with its distinct yellow fill.

      // Pass 1: batch outlines for empty, non-highlighted cells
      this.graphics.lineStyle(1, 0x334455, 1.0)
      this.graphics.beginPath()
      for (let gy = 0; gy < cells.length; gy++) {
        const row = cells[gy]
        if (row === undefined) continue
        for (let gx = 0; gx < row.length; gx++) {
          const cell = row[gx]
          if (cell === undefined) continue
          if (cell.type !== 'empty') continue
          if (this.highlightedCell !== null && this.highlightedCell.gx === gx && this.highlightedCell.gy === gy) continue
          const r = this.rot(gx, gy)
          const iso = cartToIso(r.gx, r.gy, cellW, cellH)
          const sx = iso.x + originX
          const sy = iso.y + originY
          this.graphics.moveTo(sx, sy - hh)
          this.graphics.lineTo(sx + hw, sy)
          this.graphics.lineTo(sx, sy + hh)
          this.graphics.lineTo(sx - hw, sy)
          this.graphics.closePath()
        }
      }
      this.graphics.strokePath()

      // Pass 2: filled special cells
      for (let gy = 0; gy < cells.length; gy++) {
        const row = cells[gy]
        if (row === undefined) continue
        for (let gx = 0; gx < row.length; gx++) {
          const cell = row[gx]
          if (cell === undefined) continue

          let fillColor: number | null = null
          let fillAlpha = 1.0

          if (this.highlightedCell !== null && this.highlightedCell.gx === gx && this.highlightedCell.gy === gy) {
            fillColor = 0xffff00
            fillAlpha = 0.5
          } else if (cell.type === 'obstacle') {
            fillColor = 0x8b4513
          } else if (cell.type === 'spawn') {
            fillColor = 0x00ff44
          } else if (cell.type === 'base') {
            fillColor = 0xff4444
          } else if (cell.type === 'tower') {
            fillColor = 0x555566
          }

          if (fillColor === null) continue

          const r = this.rot(gx, gy)
          const iso = cartToIso(r.gx, r.gy, cellW, cellH)
          const sx = iso.x + originX
          const sy = iso.y + originY
          this.graphics.fillStyle(fillColor, fillAlpha)
          this.graphics.beginPath()
          this.graphics.moveTo(sx, sy - hh)
          this.graphics.lineTo(sx + hw, sy)
          this.graphics.lineTo(sx, sy + hh)
          this.graphics.lineTo(sx - hw, sy)
          this.graphics.closePath()
          this.graphics.fillPath()
        }
      }
    }

    // Draw path
    const path = this.engine.getPath(this.playerId)
    if (path.length > 1) {
      this.pathGraphics.lineStyle(2, 0x00ffff, 0.6)
      this.pathGraphics.beginPath()
      const first = path[0]
      if (first !== undefined) {
        const rf = this.rot(first.gx, first.gy)
        const isoFirst = cartToIso(rf.gx, rf.gy, cellW, cellH)
        this.pathGraphics.moveTo(isoFirst.x + originX, isoFirst.y + originY)
        for (let i = 1; i < path.length; i++) {
          const node = path[i]
          if (node !== undefined) {
            const rn = this.rot(node.gx, node.gy)
            const isoNode = cartToIso(rn.gx, rn.gy, cellW, cellH)
            this.pathGraphics.lineTo(isoNode.x + originX, isoNode.y + originY)
          }
        }
        this.pathGraphics.strokePath()
      }
    }

    // Label spawn and base with procedural rects (only when no tile sprites handle it)
    if (!hasTiles) {
      const rs = this.rot(map.spawnX, map.spawnY)
      const rb = this.rot(map.baseX, map.baseY)
      const spawnIso = cartToIso(rs.gx, rs.gy, cellW, cellH)
      const baseIso = cartToIso(rb.gx, rb.gy, cellW, cellH)

      this.graphics.fillStyle(0x000000, 0.5)
      this.graphics.fillRect(spawnIso.x + originX - 12, spawnIso.y + originY - 8, 24, 16)
      this.graphics.fillRect(baseIso.x + originX - 12, baseIso.y + originY - 8, 24, 16)
    }

    this._updateRangeRing()
  }

  /** Redraws the range ring overlay. Called every frame (ring can appear/hide during hover). */
  private _updateRangeRing(): void {
    const { cellW, cellH, originX, originY } = this
    this.rangeRingGraphics.clear()
    if (this.rangeRing !== null) {
      const { gx: rgx, gy: rgy, range } = this.rangeRing
      const rr = this.rot(rgx, rgy)
      const centerIso = cartToIso(rr.gx, rr.gy, cellW, cellH)
      const cx = centerIso.x + originX
      const cy = centerIso.y + originY
      // In isometric view, range in grid cells maps to an ellipse.
      // Horizontal radius = range * (cellW/2), vertical radius = range * (cellH/2)
      const rx = range * (cellW / 2)
      const ry = range * (cellH / 2)
      // Outer glow ellipse
      this.rangeRingGraphics.lineStyle(3, 0xffffff, 0.12)
      this.rangeRingGraphics.strokeEllipse(cx, cy, rx * 2 + 6, ry * 2 + 6)
      // Main ring
      this.rangeRingGraphics.lineStyle(2, 0xffffff, 0.45)
      this.rangeRingGraphics.strokeEllipse(cx, cy, rx * 2, ry * 2)
      // Inner fill (faint)
      this.rangeRingGraphics.fillStyle(0xffffff, 0.05)
      this.rangeRingGraphics.fillEllipse(cx, cy, rx * 2, ry * 2)
    }
  }

  /**
   * Convert a **world-space** point (already transformed through camera.getWorldPoint)
   * to the logical (engine) grid coordinate, or null when the tap misses the grid.
   *
   * Two bugs existed in the original implementation that caused incorrect cell
   * detection especially at the left/right edges of the isometric diamond:
   *
   * Bug 1 — Silent clamping: isoToCart was called with (cols, rows) which clamps
   * out-of-bound values to the nearest edge cell before returning.  The bounds check
   * on the next line therefore never triggered, so a tap outside the grid would
   * silently land on a border cell (gx=0 or gx=cols-1) instead of returning null.
   * Fix: call isoToCart WITHOUT bounds arguments to get raw unclamped values; the
   * explicit check below then correctly rejects out-of-bound taps.
   *
   * Bug 2 — Rotation not inverted: when the view is rotated (rotationStep > 0) the
   * visual layout of cells does not match their logical engine coordinates.  The raw
   * isoToCart result is a *visual* coordinate; it must be rotated back by the inverse
   * rotation step to obtain the coordinate the engine expects.
   * Fix: apply rotateCoord with step = (4 - rotationStep) % 4 after the bounds check.
   *
   * Callers that have a raw Phaser pointer (screen-space) MUST convert first:
   *   const wp = this.cameras.main.getWorldPoint(pointer.x, pointer.y)
   *   const cell = gridRenderer.pointerToGrid(wp.x, wp.y)
   */
  pointerToGrid(worldX: number, worldY: number): { gx: number; gy: number } | null {
    const map = this.engine.config.map
    const adjX = worldX - this.originX
    const adjY = worldY - this.originY

    // Obtain raw, unclamped visual-grid coordinates.
    // Passing NO bounds to isoToCart is intentional — see Bug 1 above.
    const raw = isoToCart(adjX, adjY, this.cellW, this.cellH)

    // For odd rotation steps (90° / 270°) the visual grid has cols and rows swapped.
    const visualCols = (this.rotationStep & 1) === 0 ? map.cols : map.rows
    const visualRows = (this.rotationStep & 1) === 0 ? map.rows : map.cols

    if (raw.gx < 0 || raw.gx >= visualCols || raw.gy < 0 || raw.gy >= visualRows) return null

    // Convert visual coords back to unrotated logical engine coords — see Bug 2 above.
    if (this.rotationStep > 0) {
      return rotateCoord(raw.gx, raw.gy, visualCols, visualRows, (4 - this.rotationStep) & 3)
    }
    return { gx: raw.gx, gy: raw.gy }
  }

  /** Switch the rendered player's grid at runtime (spy mode) */
  setPlayerId(id: string): void {
    if (this.playerId === id) return
    this.playerId = id
    // Clear the tile sprite layer so it gets rebuilt for the new player's grid
    // (obstacle positions can differ between players).
    this._clearTileSprites()
    this._gridDirty = true
  }

  /** Cycle through 4 isometric view angles (N/E/S/W) */
  setViewAngle(step: number): void {
    this.rotationStep = ((step % 4) + 4) % 4
  }

  rotateStep(): void {
    this.rotationStep = (this.rotationStep + 1) % 4
  }

  /** Apply current rotation to a grid coord before rendering */
  private rot(gx: number, gy: number): { gx: number; gy: number } {
    const map = this.engine.config.map
    return rotateCoord(gx, gy, map.cols, map.rows, this.rotationStep)
  }

  setHighlight(gx: number, gy: number): void {
    if (this.highlightedCell?.gx !== gx || this.highlightedCell?.gy !== gy) {
      this.highlightedCell = { gx, gy }
      this._gridDirty = true
    }
  }

  clearHighlight(): void {
    if (this.highlightedCell !== null) {
      this.highlightedCell = null
      this._gridDirty = true
    }
  }

  /** Call when a tower is placed or demolished so the grid redraws on the next frame. */
  markDirty(): void {
    this._gridDirty = true
  }

  destroy(): void {
    this.scene.scale.off('resize', this._recalcLayout, this)
    this._clearTileSprites()
    this.graphics.destroy()
    this.pathGraphics.destroy()
  }
}
